import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { HolydaysHeaderSnowflakesLayer } from './HolydaysHeaderLayers/HeaderSnowflakesLayer';
import { HolydaysHeaderSpringLayer } from './HolydaysHeaderLayers/HeaderSpringLayer';
import { NotificationBell } from '../notifications/NotificationBell';
import { SupportButton } from './SupportButton';
import { NavigationBar, UserMenu, HeaderReloadButton } from './header';
import { WhatsNew } from './header/WhatsNew';
import { ThemeToggle } from './header/ThemeToggle';
import { CalcMethodologySwitch } from './header/CalcMethodologySwitch';
import { StagePresence } from './StagePresence';
import { useCurrentStageKey } from '../../hooks/useCurrentStageKey';
import { YearSelector } from '../tablecontrols/YearSelector';
import { usePlanningYear } from '../../hooks/usePlanningYear';
import { calculateHeaderNavigationLayout } from './header/headerLayout';

interface NavigationItem {
  key: string;
  label: string | React.ReactNode;
  icon: React.ReactNode;
  loading?: boolean;
}

interface AppHeaderProps {
  activeItem?: string;
  onNavigate?: (key: string) => void;
  navigationItems?: NavigationItem[];
  logo?: React.ReactNode;
  actions?: React.ReactNode[];
  loading?: boolean;
}

const HeaderActionsSkeleton: React.FC = () => (
  <div className="header-actions">
    {[28, 28, 28, 34].map((size, i) => (
      <span
        key={i}
        className="header-skeleton-circle"
        style={{ width: size, height: size }}
        aria-hidden
      />
    ))}
  </div>
);

const AppHeader: React.FC<AppHeaderProps> = ({
  activeItem = 'home',
  onNavigate,
  navigationItems = [],
  logo,
  actions = [],
  loading = false,
}) => {
  const stageKey = useCurrentStageKey();
  const [planningYear, setPlanningYear] = usePlanningYear();
  const headerContentRef = useRef<HTMLDivElement>(null);
  const brandRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<HTMLElement>(null);
  const toolsRef = useRef<HTMLDivElement>(null);

  const defaultActions = useMemo(() => [
    <SupportButton key="support" />,
    <NotificationBell key="notifications" mode="popover" showPreviewCount={5} enableQuickActions={true} />
  ], []);

  const allActions = useMemo(
    () => actions.length > 0 ? actions : defaultActions,
    [actions, defaultActions]
  );

  useLayoutEffect(() => {
    const headerContent = headerContentRef.current;
    const brand = brandRef.current;
    const navigation = navigationRef.current;
    const tools = toolsRef.current;
    const navigationTrack = navigation?.querySelector<HTMLElement>('.navigation-track');

    if (!headerContent || !brand || !navigation || !tools || !navigationTrack) {
      return;
    }

    let animationFrame = 0;

    const updateNavigationLayout = () => {
      const headerRect = headerContent.getBoundingClientRect();
      const brandRect = brand.getBoundingClientRect();
      const toolsRect = tools.getBoundingClientRect();
      const clearance = window.innerWidth <= 900 ? 4 : 8;
      const layout = calculateHeaderNavigationLayout({
        containerWidth: headerRect.width,
        leftBoundary: brandRect.right - headerRect.left + clearance,
        rightBoundary: toolsRect.left - headerRect.left - clearance,
        contentWidth: navigationTrack.scrollWidth,
      });

      navigation.style.left = `${layout.left}px`;
      navigation.style.width = `${layout.width}px`;
      navigation.style.transform = 'none';
    };

    const scheduleNavigationLayout = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(updateNavigationLayout);
    };

    const resizeObserver = new ResizeObserver(scheduleNavigationLayout);
    resizeObserver.observe(headerContent);
    resizeObserver.observe(brand);
    resizeObserver.observe(tools);
    resizeObserver.observe(navigationTrack);
    window.addEventListener('resize', scheduleNavigationLayout);
    updateNavigationLayout();

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleNavigationLayout);
    };
  }, [loading, navigationItems.length]);

  return (
    <div className="fixed-header">
      <div className="header-wrapper">
        <div ref={headerContentRef} className="header-content">
          <HolydaysHeaderSnowflakesLayer />
          <HolydaysHeaderSpringLayer />

          <div ref={brandRef} className="brand-container" data-tour-id="app-logo">
            {logo || (
              <div className="logo">PDCA+<br/>S&Op</div>
            )}
          </div>

          <NavigationBar
            items={navigationItems}
            activeItem={activeItem}
            onNavigate={onNavigate}
            containerRef={navigationRef}
          />

          <div ref={toolsRef} className="header-tools">
            {!loading && (
              <div className="header-context-actions">
                {stageKey && <StagePresence stageKey={stageKey} />}
                <HeaderReloadButton />
              </div>
            )}

            {loading ? (
              <HeaderActionsSkeleton />
            ) : (
              <div className="header-actions">
                <YearSelector
                  value={planningYear}
                  onChange={setPlanningYear}
                  size="small"
                  className="header-year-selector"
                />
                <CalcMethodologySwitch />
                <ThemeToggle />
                <WhatsNew />

                {allActions.map((action) => (
                  <React.Fragment key={(action as React.ReactElement)?.key ?? 'action'}>
                    {action}
                  </React.Fragment>
                ))}

                <UserMenu />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppHeader;
